import { Autocomplete, FormControl, TextField } from '@mui/material';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { useGetAllStatesQuery } from '../../features/geographic';
import ReadOnlyField from './readonly-field';

interface StateFieldProps<TFieldValues extends FieldValues> {
  /** The field name for the state code */
  readonly name: Path<TFieldValues>;
  /** React Hook Form control object */
  readonly control: Control<TFieldValues>;
  /** Custom label for state field (defaults to "State") */
  readonly label?: string;
  /** Whether the field is required (defaults to true) */
  readonly required?: boolean;
  /** Whether the field is in read-only mode */
  readonly isReadOnly?: boolean;
}

/**
 * A reusable component for state selection
 * Uses Autocomplete with state code and name filtering
 * Supports both editable and read-only modes
 */
const StateField = <TFieldValues extends FieldValues>({
  name,
  control,
  label = 'State',
  required = true,
  isReadOnly = false,
}: StateFieldProps<TFieldValues>) => {
  const { data: states = [], isLoading } = useGetAllStatesQuery();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const stateObject = field.value ? states.find(state => state?.code === field.value) : null;

        return isReadOnly ? (
          <ReadOnlyField
            label={label}
            value={stateObject ? stateObject.name : field.value}
            fullWidth
          />
        ) : (
          <FormControl fullWidth error={!!fieldState.error} required={required}>
            <Autocomplete
              id="state-autocomplete"
              options={states}
              getOptionLabel={option => {
                return option.code;
              }}
              disabled={isLoading}
              value={
                field.value
                  ? states.find(state => {
                      const fieldValue = typeof field.value === 'string' ? field.value : '';
                      return state.code.toLowerCase() === fieldValue.toLowerCase();
                    }) || null
                  : null
              }
              onChange={(_, newValue) => {
                if (newValue && typeof newValue === 'object' && 'code' in newValue) {
                  field.onChange(newValue.code);
                } else {
                  field.onChange('');
                }
              }}
              onInputChange={(_, inputValue, reason) => {
                if (reason === 'input') {
                  // Case insensitive matching
                  const matchedState = states.find(
                    state =>
                      state.code.toLowerCase() === inputValue.toLowerCase() ||
                      state.name.toLowerCase() === inputValue.toLowerCase()
                  );

                  if (matchedState) {
                    // Directly update the form value with the matched state's code
                    field.onChange(matchedState.code);
                  }
                }
              }}
              filterOptions={(options, state) => {
                const inputValue = state.inputValue.toLowerCase();
                return options.filter(
                  option =>
                    option.code.toLowerCase().includes(inputValue) ||
                    option.name.toLowerCase().includes(inputValue)
                );
              }}
              isOptionEqualToValue={(option, value) => {
                if (!value || !option) return false;
                if (!value.code || !option.code) return false;

                return option.code.toLowerCase() === value.code.toLowerCase();
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  label={label}
                  variant="outlined"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  required={required}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.code}>
                  {option.code}
                </li>
              )}
            />
          </FormControl>
        );
      }}
    />
  );
};

export default StateField;
